import React, { useState, useEffect, useCallback } from 'react';
import { workflowsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, GitBranch, X, GripVertical, CheckSquare, ToggleLeft, Type, Hash, Camera, List, RefreshCw, Paperclip, FileText, Image, Video } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const STEP_TYPES = [
  { type:'yes_no', label:'Yes / No', icon:ToggleLeft, color:'#10B981' },
  { type:'checklist', label:'Multiple Choice', icon:CheckSquare, color:'#0EA5E9' },
  { type:'text', label:'Text Input', icon:Type, color:'#8B5CF6' },
  { type:'number', label:'Numeric Input', icon:Hash, color:'#F97316' },
  { type:'photo', label:'Photo Required', icon:Camera, color:'#EF4444' },
];

const WORKFLOW_TYPES = ['inspection','maintenance','checklist','survey'];

function StepBuilder({ steps, setSteps, disabled }) {
  const addStep = (type) => {
    setSteps([...steps, { id:uuidv4(), type, label:'', required:true, options:type==='checklist'?['Option 1','Option 2']:[] }]);
  };
  const updateStep = (id, updates) => setSteps(steps.map(s => s.id===id ? { ...s, ...updates } : s));
  const removeStep = (id) => setSteps(steps.filter(s => s.id!==id));
  
  const handleUpload = async (id, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const tid = toast.loading('Uploading reference...');
    try {
      const res = await workflowsAPI.uploadMaterial(fd);
      const step = steps.find(s => s.id === id);
      const mats = [...(step.reference_materials || []), res.data];
      updateStep(id, { reference_materials: mats });
      toast.success('Reference uploaded', { id:tid });
    } catch {
      toast.error('Upload failed', { id:tid });
    }
    e.target.value = '';
  };

  const removeMaterial = (id, mid) => {
    const step = steps.find(s => s.id === id);
    const mats = (step.reference_materials || []).filter((_, i) => i !== mid);
    updateStep(id, { reference_materials: mats });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {/* Step Type Picker */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:4 }}>
        {STEP_TYPES.map(({ type, label, icon:Icon, color }) => (
          <button key={type} onClick={() => addStep(type)}
            style={{
              display:'flex', alignItems:'center', gap:6, padding:'6px 12px',
              borderRadius:'var(--r-md)', border:`1.5px solid ${color}30`,
              background:`${color}10`, color, fontWeight:600, fontSize:12, cursor:'pointer'
            }}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* Steps List */}
      {steps.length === 0 && (
        <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:13, background:'var(--bg-muted)', borderRadius:'var(--r-md)', border:'1px dashed var(--border)' }}>
          Click a step type above to add your first step
        </div>
      )}
      {steps.map((step, i) => {
        const typeInfo = STEP_TYPES.find(t => t.type === step.type) || STEP_TYPES[0];
        return (
          <div key={step.id} style={{
            background:'var(--bg-muted)', borderRadius:'var(--r-md)', border:'1px solid var(--border)',
            borderLeft:`3px solid ${typeInfo.color}`, padding:'12px 14px'
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <GripVertical size={14} color="var(--text-muted)" />
              <div style={{ width:24, height:24, borderRadius:6, background:`${typeInfo.color}20`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <typeInfo.icon size={12} color={typeInfo.color} />
              </div>
              <span style={{ fontSize:11, fontWeight:700, color:typeInfo.color, textTransform:'uppercase', letterSpacing:0.4 }}>Step {i+1} · {typeInfo.label}</span>
              <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
                <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'var(--text-secondary)', cursor:'pointer' }}>
                  <input type="checkbox" checked={step.required} onChange={e => updateStep(step.id, { required:e.target.checked })} />
                  Required
                </label>
                <button style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', display:'flex' }} onClick={() => removeStep(step.id)}>
                  <X size={14} />
                </button>
              </div>
            </div>
            <input className="form-input" placeholder="Step label / question..." value={step.label}
              onChange={e => updateStep(step.id, { label:e.target.value })}
              style={{ marginBottom: step.type==='checklist'?8:0, fontSize:13 }} />
            {step.type === 'number' && (
              <input className="form-input" placeholder="Unit (e.g. °C, %, bar)" value={step.unit||''}
                onChange={e => updateStep(step.id, { unit:e.target.value })}
                style={{ marginTop:8, maxWidth:160, fontSize:13 }} />
            )}
            {step.type === 'checklist' && (
              <div style={{ display:'flex', flexDirection:'column', gap:6, marginTop:8 }}>
                {(step.options||[]).map((opt, oi) => (
                  <div key={oi} style={{ display:'flex', gap:6 }}>
                    <input className="form-input" value={opt} style={{ fontSize:13 }}
                      onChange={e => { const o=[...step.options]; o[oi]=e.target.value; updateStep(step.id,{options:o}); }}
                      placeholder={`Option ${oi+1}`} />
                    <button className="btn-icon" onClick={() => { const o=step.options.filter((_,j)=>j!==oi); updateStep(step.id,{options:o}); }}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" style={{ alignSelf:'flex-start' }}
                  onClick={() => updateStep(step.id, { options:[...step.options,'New option'] })}>
                  <Plus size={12} /> Add Option
                </button>
              </div>
            )}

            {/* Reference Materials */}
            <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Reference Materials</span>
                <label className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}>
                  <Paperclip size={11} /> Attach
                  <input type="file" hidden onChange={e => handleUpload(step.id, e)} />
                </label>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(step.reference_materials || []).map((m, mi) => (
                  <div key={mi} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--bg-card)', padding: '4px 8px', borderRadius: 6,
                    border: '1px solid var(--border)', fontSize: 12
                  }}>
                    {m.type === 'image' ? <Image size={11} /> : m.type === 'video' ? <Video size={11} /> : <FileText size={11} />}
                    <span className="truncate" style={{ maxWidth: 120 }}>{m.name}</span>
                    <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                      onClick={() => removeMaterial(step.id, mi)}>
                      <X size={10} />
                    </button>
                  </div>
                ))}
                {(!step.reference_materials || step.reference_materials.length === 0) && (
                  <span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--text-muted)' }}>No reference materials attached</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WorkflowModal({ workflow, onClose, onSave }) {
  const [form, setForm] = useState({
    name: workflow?.name || '', description: workflow?.description || '',
    type: workflow?.type || 'inspection', estimated_duration: workflow?.estimated_duration || 30,
  });
  const [steps, setSteps] = useState(workflow?.steps || []);
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.name) return toast.error('Workflow name required');
    if (steps.length === 0) return toast.error('Add at least one step');
    const incomplete = steps.filter(s => !s.label.trim());
    if (incomplete.length) return toast.error('All steps must have a label');
    setLoading(true);
    try {
      if (workflow) await workflowsAPI.update(workflow.id, { ...form, steps });
      else await workflowsAPI.create({ ...form, steps });
      toast.success(workflow ? 'Workflow updated!' : 'Workflow created!');
      onSave();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-xl">
        <div className="modal-header">
          <div>
            <div className="modal-title">{workflow ? 'Edit Workflow' : 'Create Workflow Template'}</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }}>Build an inspection or maintenance checklist</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:24 }}>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div className="form-group">
                <label className="form-label">Template Name *</label>
                <input className="form-input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. HVAC Monthly Inspection" />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-select" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                  {WORKFLOW_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Estimated Duration (minutes)</label>
                <input className="form-input" type="number" value={form.estimated_duration} onChange={e=>setForm(f=>({...f,estimated_duration:parseInt(e.target.value)}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={4} placeholder="Purpose of this workflow..." />
              </div>
              <div style={{ padding:14, background:'var(--bg-muted)', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text)', marginBottom:8 }}>Summary</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{steps.length} step{steps.length!==1?'s':''} · ~{form.estimated_duration} min</div>
                <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>
                  {steps.filter(s=>s.required).length} required · {steps.filter(s=>!s.required).length} optional
                </div>
              </div>
            </div>
            <div>
              <label className="form-label" style={{ marginBottom:12, display:'block' }}>Workflow Steps</label>
              <StepBuilder steps={steps} setSteps={setSteps} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <div className="spinner" style={{borderColor:'rgba(255,255,255,0.3)',borderTopColor:'#fff'}} /> : <GitBranch size={14} />}
            {workflow ? 'Save Changes' : 'Create Workflow'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    workflowsAPI.list().then(r => setWorkflows(r.data.workflows)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (wf) => {
    if (!window.confirm(`Delete "${wf.name}"?`)) return;
    try { await workflowsAPI.delete(wf.id); toast.success('Workflow deleted'); load(); }
    catch { toast.error('Cannot delete — workflow may be in use'); }
  };

  const TYPE_COLORS = { inspection:'#0EA5E9', maintenance:'#F59E0B', checklist:'#10B981', survey:'#8B5CF6' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:14, color:'var(--text-muted)' }}>{workflows.length} template{workflows.length!==1?'s':''}</div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn-icon" onClick={load}><RefreshCw size={15} /></button>
          <button className="btn btn-primary" onClick={() => { setSelected(null); setModal('form'); }}>
            <Plus size={15} /> New Workflow
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid-auto">{[1,2,3].map(i => <div key={i} className="card" style={{padding:20}}><div className="skeleton" style={{height:140}} /></div>)}</div>
      ) : workflows.length === 0 ? (
        <div className="card"><div className="empty-state">
          <GitBranch size={40} className="empty-icon" />
          <div className="empty-title">No workflow templates</div>
          <div className="empty-sub">Create inspection and maintenance checklists for your team</div>
          <button className="btn btn-primary" onClick={() => setModal('form')}><Plus size={14} /> Create Workflow</button>
        </div></div>
      ) : (
        <div className="grid-auto animate-in">
          {workflows.map((wf, i) => (
            <div key={wf.id} className="card" style={{ display:'flex', flexDirection:'column', animationDelay:`${i*40}ms` }}>
              <div style={{
                padding:'18px 20px', borderBottom:'1px solid var(--border)',
                background:`${TYPE_COLORS[wf.type] || '#0EA5E9'}08`
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:TYPE_COLORS[wf.type], textTransform:'uppercase', letterSpacing:0.6 }}>{wf.type}</div>
                    <div style={{ fontSize:15, fontWeight:800, color:'var(--text)', marginTop:3 }}>{wf.name}</div>
                  </div>
                  <div style={{ width:38, height:38, borderRadius:'var(--r-md)', background:`${TYPE_COLORS[wf.type]}20`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <GitBranch size={18} color={TYPE_COLORS[wf.type]} />
                  </div>
                </div>
                {wf.description && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:8 }}>{wf.description}</div>}
              </div>

              <div style={{ padding:'14px 20px', flex:1, display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ display:'flex', gap:12 }}>
                  <div style={{ flex:1, textAlign:'center', background:'var(--bg-muted)', borderRadius:'var(--r-sm)', padding:'8px 4px' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'var(--text)' }}>{wf.steps?.length || 0}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>Steps</div>
                  </div>
                  <div style={{ flex:1, textAlign:'center', background:'var(--bg-muted)', borderRadius:'var(--r-sm)', padding:'8px 4px' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'var(--text)' }}>{wf.estimated_duration || '—'}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>Minutes</div>
                  </div>
                  <div style={{ flex:1, textAlign:'center', background:'var(--bg-muted)', borderRadius:'var(--r-sm)', padding:'8px 4px' }}>
                    <div style={{ fontSize:18, fontWeight:800, color:'var(--accent)' }}>{wf.usage_count || 0}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)' }}>Jobs</div>
                  </div>
                </div>

                {/* Step preview */}
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {(wf.steps||[]).slice(0,3).map((s, i) => {
                    const t = STEP_TYPES.find(x=>x.type===s.type)||STEP_TYPES[0];
                    return (
                      <div key={s.id||i} style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <t.icon size={11} color={t.color} />
                        <span style={{ fontSize:12, color:'var(--text-secondary)' }} className="truncate">{s.label || 'Untitled step'}</span>
                      </div>
                    );
                  })}
                  {wf.steps?.length > 3 && <div style={{ fontSize:11, color:'var(--text-muted)' }}>+{wf.steps.length-3} more steps</div>}
                </div>
              </div>

              <div style={{ padding:'12px 20px', borderTop:'1px solid var(--border)', display:'flex', gap:8 }}>
                <button className="btn btn-ghost btn-sm" style={{ flex:1, justifyContent:'center' }}
                  onClick={() => { setSelected(wf); setModal('form'); }}>
                  <Edit2 size={12} /> Edit
                </button>
                <button className="btn-icon" style={{ color:'var(--danger)' }} onClick={() => handleDelete(wf)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal === 'form' && (
        <WorkflowModal workflow={selected} onClose={() => setModal(null)} onSave={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
